import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { DEFAULT_PAGES } from "../lib/landingConfig";

export default function InfoPage() {
  const { slug } = useParams();
  const [platform, setPlatform] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setPlatform(await api.getPlatform()); } catch { setPlatform({}); } finally { setLoading(false); }
    })();
  }, []);

  const page = useMemo(() => {
    const pages = platform?.landing?.pages;
    const list = Array.isArray(pages) && pages.length ? pages : DEFAULT_PAGES;
    return list.find((p) => p.slug === slug) || DEFAULT_PAGES.find((p) => p.slug === slug) || null;
  }, [platform, slug]);

  const html = useMemo(() => {
    const body = page?.body || "";
    // If plain text (no HTML tags), preserve line breaks; otherwise treat as rich HTML.
    const isHtml = /<[a-z][\s\S]*>/i.test(body);
    let out = isHtml
      ? body
      : body.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c])).replace(/\n/g, "<br/>");
    // strip scripts / inline event handlers for safety
    out = out.replace(/<script[\s\S]*?<\/script>/gi, "").replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
    return out;
  }, [page]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div dir="rtl" className="min-h-screen bg-slate-950 text-slate-100 font-body flex flex-col" data-testid="info-page">
      <header className="border-b border-white/10 bg-[#070d16]">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 py-4 flex items-center justify-between">
          <Logo size="md" textClass="text-white" />
          <a href="/" data-testid="info-back-home" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-emerald-300 transition">
            العودة للرئيسية <ArrowLeft className="h-4 w-4" />
          </a>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-4 sm:px-8 py-14">
          {page ? (
            <article data-testid={`info-page-${page.slug}`}>
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mb-6">{page.title}</h1>
              <div className="prose-page text-slate-300 leading-relaxed text-base" dangerouslySetInnerHTML={{ __html: html }} />
            </article>
          ) : (
            <div className="text-center py-20" data-testid="info-page-missing">
              <h1 className="font-heading text-2xl font-bold mb-3">الصفحة غير متوفرة</h1>
              <p className="text-slate-400">لم يتم العثور على المحتوى المطلوب.</p>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-white/10 py-6 text-center text-xs text-slate-500">
        {platform?.name || "WorkZone"} © {new Date().getFullYear()}
      </footer>
    </div>
  );
}
